import { saveVerificationPhone, phoneSavePatch } from "@/features/phone-verification/phoneVerificationService.js";
// src/pages/onboarding/doctor/DoctorOnboarding.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { loadProgress, saveProgress } from "@/lib/persistStep";
import supabase from "@/services/supabaseClient";
import { useAuth } from "@/contexts/AuthContext";
import { splitE164ToCcLocal } from "@/utils/phone.js";

// Intro (phone/otp/name/about/profile complete)
import StepPhone from "@/pages/onboarding/shared/steps/StepPhone.jsx";
import StepOtp from "@/pages/onboarding/shared/steps/StepOtp.jsx";
import StepName from "@/pages/onboarding/shared/steps/StepName.jsx";
import StepPasscodeCreate from "@/pages/onboarding/shared/steps/StepPasscodeCreate.jsx";
import StepPasscodeConfirm from "@/pages/onboarding/shared/steps/StepPasscodeConfirm.jsx";
import StepDob from "@/pages/onboarding/shared/steps/StepDob.jsx";
import StepDoneDoctor from "@/pages/onboarding/doctor/steps/StepDoneDoctor.jsx";

// Profile
import StepIDPassport from "@/pages/onboarding/shared/steps/StepIDPassport.jsx";
import StepLanguages from "@/pages/onboarding/shared/steps/StepLanguages.jsx";
import StepSpecialty from "@/pages/onboarding/doctor/steps/StepSpecialty.jsx";
import StepSubSpecialty from "@/pages/onboarding/doctor/steps/StepSubSpecialty.jsx";
import StepRegUpload from "@/pages/onboarding/doctor/steps/StepRegUpload.jsx";
import StepFormation from "@/pages/onboarding/doctor/steps/StepFormation.jsx";
import StepRegDetails from "@/pages/onboarding/doctor/steps/StepRegDetails.jsx";
import StepAbout from "@/pages/onboarding/doctor/steps/StepAbout.jsx";
import StepProfileComplete from "@/pages/onboarding/doctor/steps/StepProfileComplete.jsx";
import { markSettingsWelcomePending } from "@/lib/settingsWelcomeState.js";
import { findVerificationDocument } from "@/lib/doctorVerificationDocuments";
import { writeOnboardingEvent, writeUserActivityEvent } from "@/services/userTruthTracking.js";
import { logAuthAttempt } from "@/services/authAttemptService.js";
import {
  ENABLE_ONBOARDING_PHONE_OTP,
  buildOnboardingIntroSteps,
  normalizeIntroStepForPhoneOtpFlag,
} from "@/pages/onboarding/onboardingPhoneOtpFeature.js";
import {
  getDocumentNavigationTelemetry,
  getPrivacySafeTelemetryRoute,
} from "@/lib/onboardingNavigationTelemetry.js";

const POST_AUTH_RENDER_SETTLE_MS = 500;
const POST_AUTH_BLANK_TIMEOUT_MS = 9000;
let doctorOnboardingShellMountsForDocument = 0;

const formatToIso = (rawDate) => {
  const value = String(rawDate || "").trim();
  if (!value) return "";

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

  const dotted = value.match(/^(\d{2})[./-](\d{2})[./-](\d{4})$/);
  if (dotted) {
    const [, dd, mm, yyyy] = dotted;
    return `${yyyy}-${mm}-${dd}`;
  }

  if (/^\d{6}$/.test(value)) {
    const yy = parseInt(value.slice(0, 2), 10);
    const mm = value.slice(2, 4);
    const dd = value.slice(4, 6);
    const year = yy > 50 ? `19${yy}` : `20${yy}`;
    return `${year}-${mm}-${dd}`;
  }

  return "";
};

const toNonEmptyString = (value) => {
  const next = String(value || "").trim();
  return next || "";
};

function normalizeStringList(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => String(item || "").trim())
      .filter(Boolean);
  }
  if (typeof value === "string") {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function orderLanguagesWithPreferred(items, preferred) {
  const preferredClean = String(preferred || "").trim();
  const deduped = Array.from(
    new Map(
      [
        ...(preferredClean ? [preferredClean] : []),
        ...normalizeStringList(items),
      ]
        .map((item) => String(item || "").trim())
        .filter(Boolean)
        .map((item) => [item.toLowerCase(), item])
    ).values()
  );

  if (!preferredClean) return deduped;

  return [
    preferredClean,
    ...deduped.filter((item) => item.toLowerCase() !== preferredClean.toLowerCase()),
  ];
}

const normalizeDoctorRegDetailsFromProfile = (profileRow) => {
  const src = profileRow && typeof profileRow === "object" ? profileRow : {};
  const regDoc = findVerificationDocument(src?.verification_documents, "medical_license") || {};
  const registerDetails =
    regDoc?.register_details && typeof regDoc.register_details === "object"
      ? regDoc.register_details
      : {};
  const ocrFields =
    regDoc?.ocr_fields && typeof regDoc.ocr_fields === "object" ? regDoc.ocr_fields : {};

  const number =
    toNonEmptyString(src?.register_number) ||
    toNonEmptyString(src?.medical_license_number) ||
    toNonEmptyString(registerDetails?.number) ||
    toNonEmptyString(ocrFields?.document_number);

  const issuer =
    toNonEmptyString(src?.register_country) ||
    toNonEmptyString(registerDetails?.issuer) ||
    toNonEmptyString(ocrFields?.issuer);

  const issueDate =
    formatToIso(registerDetails?.issueDate || "") ||
    formatToIso(ocrFields?.issue_date || "");

  const expiryDate =
    formatToIso(registerDetails?.expiryDate || "") ||
    formatToIso(ocrFields?.expiry_date || "");

  const yearsExperience =
    toNonEmptyString(registerDetails?.yearsExperience) ||
    toNonEmptyString(src?.years_of_experience) ||
    toNonEmptyString(src?.years_experience);

  return {
    number,
    issuer,
    issueDate,
    expiryDate,
    yearsExperience,
  };
};

const EMPTY_ID_VERIFICATION = {
  captured: null,
  uploadMeta: null,
  verifyStatus: "idle",
  verifyMsg: "",
  ocrStatus: "idle",
  ocrMsg: "",
  ocrSummary: null,
  stagingBypassEligible: false,
  stagingTesterEmail: "",
  stagingBypass: false,
  stagingDeferred: false,
};

function normalizeIdVerificationState(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  return {
    captured:
      src.captured && typeof src.captured === "object"
        ? {
            name: String(src.captured.name || "").trim(),
            size: Number(src.captured.size || 0) || 0,
            type: String(src.captured.type || "").trim(),
          }
        : null,
    uploadMeta: src.uploadMeta && typeof src.uploadMeta === "object" ? src.uploadMeta : null,
    verifyStatus: String(src.verifyStatus || "idle").trim() || "idle",
    verifyMsg: String(src.verifyMsg || "").trim(),
    ocrStatus: String(src.ocrStatus || "idle").trim() || "idle",
    ocrMsg: String(src.ocrMsg || "").trim(),
    ocrSummary: src.ocrSummary && typeof src.ocrSummary === "object" ? src.ocrSummary : null,
    stagingBypassEligible: src.stagingBypassEligible === true,
    stagingTesterEmail: String(src.stagingTesterEmail || "").trim().toLowerCase(),
    stagingBypass: src.stagingBypass === true,
    stagingDeferred: src.stagingDeferred === true,
  };
}

function hasStoredPasscode(profileRow) {
  return !!(
    profileRow?.passcode_hash ||
    profileRow?.passcode_set_at ||
    profileRow?.passcode_updated_at ||
    profileRow?.passcode
  );
}

const LS_KEY = "onb_doctor_v2";
const BASE_INTRO_STEPS = [
  "name",
  "phone",
  "otp",
  "about",
  "profileComplete",
];
const INTRO_STEPS = buildOnboardingIntroSteps(BASE_INTRO_STEPS);
const DOCTOR_SETUP_REASSURANCE =
  "As you proceed, each time you tap Continue, the previous step is saved.";
const DOCTOR_SETUP_RESUME_LINE = "Continue your clinician setup.";

function shouldUseDeferredOnboardingProfileSteps() {
  return false;
}

function buildSeedState(ownerUserId = "") {
  return {
    ownerUserId: String(ownerUserId || "").trim(),
    phase: "intro",
    step: 0,
    profileStep: 0,
    firstName: "",
    lastName: "",
    salutation: "",
    email: "",
    phone: "",
    phoneCc: "+27",
    e164: null,
    agreed: false,
    passcodeDraft: "",
    passcodeConfirm: "",
    dateOfBirth: "",
    languages: [],
    preferredLanguage: "",
    specialties: [],
    subspecialties: [],
    ocrSummary: null,
    regUpload: null,
    regDetails: {
      number: "",
      issuer: "",
      issueDate: "",
      expiryDate: "",
      yearsExperience: "",
    },
    formation: null,
    idDoc: null,
    idVerification: { ...EMPTY_ID_VERIFICATION },
    about: "",
  };
}

function hasMeaningfulDoctorProgress(value) {
  if (!value || typeof value !== "object") return false;
  if (value.phase === "profile") return true;
  if (Number(value.step || 0) > 0 || Number(value.profileStep || 0) > 0) return true;
  return [
    "firstName",
    "lastName",
    "phone",
    "e164",
    "dateOfBirth",
    "idDoc",
    "about",
  ].some((key) => String(value[key] || "").trim());
}

function mapDoctorOnboardingStepKey({ phase, introStep, profileStep }) {
  if (phase === "intro") {
    switch (introStep) {
      case "name":
        return "step_name";
      case "phone":
        return "step_phone";
      case "otp":
        return "step_otp";
      case "about":
        return "profile_about";
      case "profileComplete":
        return "profile_complete";
      case "passcodeCreate":
        return "step_passcode_create";
      case "passcodeConfirm":
        return "step_passcode_confirm";
      case "dob":
        return "step_dob";
      case "done":
        return "step_done";
      default:
        return "";
    }
  }

  switch (profileStep) {
    case 0:
      return "profile_id";
    case 1:
      return "profile_languages";
    case 2:
      return "profile_specialty";
    case 3:
      return "profile_subspecialty";
    case 4:
      return "profile_reg_upload";
    case 5:
      return "profile_formation";
    case 6:
      return "profile_reg_details";
    case 7:
      return "profile_about";
    case 8:
      return "profile_complete";
    default:
      return "";
  }
}

function isElementVisible(element) {
  if (!element || typeof element.getBoundingClientRect !== "function") return false;
  const style = window.getComputedStyle?.(element);
  if (!style || style.visibility === "hidden" || style.display === "none" || Number(style.opacity) === 0) {
    return false;
  }
  const rect = element.getBoundingClientRect();
  return rect.width > 0 && rect.height > 0;
}

function isElementInViewport(element) {
  if (!isElementVisible(element)) return false;
  const rect = element.getBoundingClientRect();
  const viewport = window.visualViewport || {};
  const width = Number(viewport.width || window.innerWidth || document.documentElement?.clientWidth || 0);
  const height = Number(viewport.height || window.innerHeight || document.documentElement?.clientHeight || 0);
  return rect.bottom > 0 && rect.right > 0 && rect.top < height && rect.left < width;
}

function readCookiePostAuthSnapshot() {
  let consentState = null;
  try {
    consentState = window.afrimedCookies?.get?.() || null;
  } catch {
    consentState = null;
  }

  const cookieBannerVisible = Array.from(
    document.querySelectorAll("h1,h2,h3,p,button")
  ).some((element) => {
    if (!isElementVisible(element)) return false;
    const text = String(element.textContent || "");
    return (
      text.includes("Cookie Preferences") ||
      text.includes("Reject optional") ||
      text.includes("Accept all")
    );
  });

  return {
    cookieBannerVisible,
    cookieConsentDecided: consentState?.decided === true,
    cookieChoices: consentState?.choices && typeof consentState.choices === "object"
      ? {
          strictly: consentState.choices.strictly === true,
          statistics: consentState.choices.statistics === true,
          performance: consentState.choices.performance === true,
          functionality: consentState.choices.functionality === true,
          targeting: consentState.choices.targeting === true,
          social: consentState.choices.social === true,
          beacons: consentState.choices.beacons === true,
        }
      : null,
  };
}

function findVisiblePostAuthCta({ requireEnabled = false } = {}) {
  const ctaPattern =
    /\b(continue|verify|next|start|save|done|skip|request code|send code|confirm|complete)\b/i;
  const candidates = Array.from(
    document.querySelectorAll("button,a,[role='button']")
  );
  return candidates.find((element) => {
    if (!isElementInViewport(element)) return false;
    if (
      requireEnabled &&
      (element.disabled || element.getAttribute("aria-disabled") === "true")
    ) {
      return false;
    }
    return ctaPattern.test(String(element.textContent || element.getAttribute("aria-label") || ""));
  }) || null;
}

function isActionableElement(element) {
  if (!element) return false;
  return !(element.disabled || element.getAttribute("aria-disabled") === "true");
}

function PostAuthSetupFallback({ onRetry, onSignIn }) {
  return (
    <div className="min-h-[100dvh] bg-[rgb(var(--clr-surface))] grid place-items-center px-4">
      <div className="w-full max-w-[420px] rounded-[18px] border border-[rgba(var(--clr-outline),0.22)] bg-[rgb(var(--clr-surface-cont-highest))] p-6 text-center shadow-sm">
        <h1 className="text-title-large text-on-surface">
          We’re preparing your AfriMed™ setup.
        </h1>
        <p className="mt-3 text-body-medium text-on-surface-var">
          Something is taking longer than expected.
        </p>
        <div className="mt-6 grid gap-3">
          <button type="button" onClick={onRetry} className="h-12 rounded-[14px] bg-[rgb(var(--afrimed-primary))] px-5 text-title-medium text-[rgb(var(--afrimed-on-primary))]">
            Retry
          </button>
          <button type="button" onClick={onSignIn} className="h-12 rounded-[14px] bg-[rgba(var(--clr-on-surface),0.06)] px-5 text-title-medium text-on-surface">
            Go to sign in
          </button>
          <a href="mailto:ndaba@afrimed.tech?subject=AfriMed%20setup%20support" className="inline-flex h-12 items-center justify-center rounded-[14px] text-title-medium text-[rgb(var(--afrimed-primary))]">
            Contact support
          </a>
        </div>
      </div>
    </div>
  );
}

export default function DoctorOnboarding({ backToCategory }) {
  const navigate = useNavigate();
  const { user, profile, loading } = useAuth();
  const [sp] = useSearchParams();
  const forceFresh = sp.get("fresh") === "1";

  // ---- Restore & sanitize BEFORE state init ----
const rawRestoredProgress = loadProgress(LS_KEY) ?? {};
const renderUserId = String(user?.id || "").trim();
const restoredOwnerUserId = String(rawRestoredProgress?.ownerUserId || "").trim();
const restoredRaw =
  renderUserId && restoredOwnerUserId === renderUserId
    ? rawRestoredProgress
    : buildSeedState(renderUserId);
const clamp = (n, lo, hi) =>
  Number.isFinite(n) ? Math.min(Math.max(n, lo), hi) : lo;

const PROFILE_MAX = 8;
const INTRO_MAX = Math.max(0, INTRO_STEPS.length - 1);

const safePhase =
  restoredRaw.phase === "intro" || restoredRaw.phase === "profile"
    ? restoredRaw.phase
    : "intro";

const restored = {
  phase: safePhase,
  step: normalizeIntroStepForPhoneOtpFlag(restoredRaw.step, BASE_INTRO_STEPS, INTRO_STEPS),
  profileStep: clamp(restoredRaw.profileStep, 0, PROFILE_MAX),
  firstName: restoredRaw.firstName ?? "",
  lastName: restoredRaw.lastName ?? "",
  salutation: restoredRaw.salutation ?? "",

  email: restoredRaw.email ?? "",
  phone: restoredRaw.phone ?? "",
  phoneCc: restoredRaw.phoneCc ?? "+27",
  e164: restoredRaw.e164 ?? null,
  agreed: !!restoredRaw.agreed,
  passcodeDraft: restoredRaw.passcodeDraft ?? "",
  passcodeConfirm: restoredRaw.passcodeConfirm ?? "",
  dateOfBirth: restoredRaw.dateOfBirth ?? "",

  languages: restoredRaw.languages ?? [],
  preferredLanguage: String(restoredRaw.preferredLanguage || "").trim(),
  specialties: restoredRaw.specialties ?? [],
  subspecialties: restoredRaw.subspecialties ?? [],
  ocrSummary: restoredRaw.ocrSummary ?? null,
  regUpload: restoredRaw.regUpload ?? null,
  regDetails: restoredRaw.regDetails ?? {
    number: "",
    issuer: "",
    issueDate: "",
    expiryDate: "",
    yearsExperience: "",
  },
  formation: restoredRaw.formation ?? null,
  idDoc: restoredRaw.idDoc ?? null,
  idVerification: normalizeIdVerificationState(restoredRaw.idVerification),
  about: restoredRaw.about ?? "",
};
const initialResumeContext =
  !forceFresh &&
  renderUserId &&
  restoredOwnerUserId === renderUserId &&
  hasMeaningfulDoctorProgress(rawRestoredProgress);

  // ---- State ----
  const [phase, setPhase] = useState(restored.phase);
  const [step, setStep] = useState(restored.step);
  const [profileStep, setProfileStep] = useState(restored.profileStep);
  const [firstName, setFirstName] = useState(restored.firstName);
  const [lastName, setLastName] = useState(restored.lastName);
  const [salutation, setSalutation] = useState(restored.salutation);

  const [email, setEmail] = useState(restored.email);
  const [phone, setPhone] = useState(restored.phone);
  const [phoneCc, setPhoneCc] = useState(restored.phoneCc);
  const [e164, setE164] = useState(restored.e164);
  const [agreed, setAgreed] = useState(restored.agreed);
  const [passcodeDraft, setPasscodeDraft] = useState(restored.passcodeDraft);
  const [passcodeConfirm, setPasscodeConfirm] = useState(restored.passcodeConfirm);
  const [dateOfBirth, setDateOfBirth] = useState(restored.dateOfBirth);

  const [languages, setLanguages] = useState(restored.languages);
  const [preferredLanguage, setPreferredLanguage] = useState(restored.preferredLanguage);
  const [specialties, setSpecialties] = useState(restored.specialties);
  const [subspecialties, setSubspecialties] = useState(restored.subspecialties);
  const [ocrSummary, setOcrSummary] = useState(restored.ocrSummary);
  const [regUpload, setRegUpload] = useState(restored.regUpload);
  const [formation, setFormation] = useState(restored.formation);
  const [idDoc, setIdDoc] = useState(restored.idDoc);
  const [idVerification, setIdVerification] = useState(restored.idVerification);
  const [about, setAbout] = useState(restored.about);
  const [regDetails, setRegDetails] = useState(restored.regDetails);
  const [postAuthFallbackVisible, setPostAuthFallbackVisible] = useState(false);
  const [resumeContextVisible, setResumeContextVisible] = useState(initialResumeContext);
  const persistedProfileRegDetails = normalizeDoctorRegDetailsFromProfile(profile);
  
  // ---- Auth truth (server) ----
  const [authUser, setAuthUser] = useState(null);
  const authEmailConfirmedAt =
    authUser?.email_confirmed_at || authUser?.emailConfirmedAt || null;
  const authPhoneConfirmedAt =
    authUser?.phone_confirmed_at || authUser?.phoneConfirmedAt || null;

  const isAuthEmailVerified = !!authEmailConfirmedAt;
  const isAuthPhoneVerified = !!authPhoneConfirmedAt;
  const introSteps = React.useMemo(() => INTRO_STEPS, []);
  const afterPhoneIntroStep = ENABLE_ONBOARDING_PHONE_OTP ? "otp" : "passcodeCreate";
  const introStepIndex = React.useCallback(
    (stepName) => {
      const idx = introSteps.indexOf(stepName);
      return idx === -1 ? 0 : idx;
    },
    [introSteps]
  );
  const loggedStepKeysRef = useRef(new Set());
  const shellMountRecordedRef = useRef(false);
  const postAuthDiagnosticsRef = useRef({
    started: false,
    completed: false,
    ctaVisible: false,
    ctaLogged: false,
    blankTimeout: false,
    cookieChoice: false,
    cookieBannerVisible: false,
  });

  // ---- Force-fresh when requested (direct URL) ----
  const didFresh = useRef(false);
  useEffect(() => {
    if (!forceFresh || didFresh.current) return;
    didFresh.current = true;

    try {
      localStorage.removeItem(LS_KEY);
    } catch {}

    const seed = buildSeedState(user?.id || authUser?.id || "");

    saveProgress(LS_KEY, seed);

    setPhase(seed.phase);
    setStep(seed.step);
    setProfileStep(seed.profileStep);
    setFirstName(seed.firstName);
    setLastName(seed.lastName);
    setSalutation(seed.salutation);

    setEmail(seed.email);
    setPhone(seed.phone);
    setPhoneCc(seed.phoneCc);
    setE164(seed.e164);
    setAgreed(seed.agreed);
    setPasscodeDraft(seed.passcodeDraft);
    setPasscodeConfirm(seed.passcodeConfirm);
    setDateOfBirth(seed.dateOfBirth);

    setLanguages(seed.languages);
    setSpecialties(seed.specialties);
    setSubspecialties(seed.subspecialties);
    setOcrSummary(seed.ocrSummary);
    setRegUpload(seed.regUpload);
    setFormation(seed.formation);
    setIdDoc(seed.idDoc);
    setIdVerification(seed.idVerification);
    setAbout(seed.about);

    }, [forceFresh, user?.id, authUser?.id]);

  useEffect(() => {
    const currentUserId = String(user?.id || authUser?.id || "").trim();
    if (!currentUserId) return;

    const stored = loadProgress(LS_KEY) ?? {};
    const ownerUserId = String(stored?.ownerUserId || "").trim();
    if (ownerUserId === currentUserId) {
      setResumeContextVisible(!forceFresh && hasMeaningfulDoctorProgress(stored));
      setPhase(stored.phase === "profile" ? "profile" : "intro");
      setStep(clamp(stored.step, 0, INTRO_MAX));
      setProfileStep(clamp(stored.profileStep, 0, PROFILE_MAX));
      setFirstName(stored.firstName ?? "");
      setLastName(stored.lastName ?? "");
      setEmail(stored.email ?? "");
      setPhone(stored.phone ?? "");
      setPhoneCc(stored.phoneCc ?? "+27");
      setE164(stored.e164 ?? null);
      setAgreed(!!stored.agreed);
      setPasscodeDraft(stored.passcodeDraft ?? "");
      setPasscodeConfirm(stored.passcodeConfirm ?? "");
      setDateOfBirth(stored.dateOfBirth ?? "");
      setLanguages(stored.languages ?? []);
      setPreferredLanguage(String(stored.preferredLanguage || "").trim());
      setSpecialties(stored.specialties ?? []);
      setSubspecialties(stored.subspecialties ?? []);
      setOcrSummary(stored.ocrSummary ?? null);
      setRegUpload(stored.regUpload ?? null);
      setFormation(stored.formation ?? null);
      setIdDoc(stored.idDoc ?? null);
      setIdVerification(normalizeIdVerificationState(stored.idVerification));
      setAbout(stored.about ?? "");
      setRegDetails(stored.regDetails ?? {
        number: "",
        issuer: "",
        issueDate: "",
        expiryDate: "",
        yearsExperience: "",
      });
      return;
    }

    const seed = buildSeedState(currentUserId);
    setResumeContextVisible(false);
    saveProgress(LS_KEY, seed);
    setPhase(seed.phase);
    setStep(seed.step);
    setProfileStep(seed.profileStep);
    setFirstName(seed.firstName);
    setLastName(seed.lastName);
    setEmail(seed.email);
    setPhone(seed.phone);
    setPhoneCc(seed.phoneCc);
    setE164(seed.e164);
    setAgreed(seed.agreed);
    setPasscodeDraft(seed.passcodeDraft);
    setPasscodeConfirm(seed.passcodeConfirm);
    setDateOfBirth(seed.dateOfBirth);
    setLanguages(seed.languages);
    setSpecialties(seed.specialties);
    setSubspecialties(seed.subspecialties);
    setOcrSummary(seed.ocrSummary);
    setRegUpload(seed.regUpload);
    setFormation(seed.formation);
    setIdDoc(seed.idDoc);
    setIdVerification(seed.idVerification);
    setAbout(seed.about);
  }, [user?.id, authUser?.id]);

  // ---- Auth bootstrap + skip logic (runs once) ----
  const didAuthBootstrap = useRef(false);
  useEffect(() => {
    if (didAuthBootstrap.current) return;
    didAuthBootstrap.current = true;

    (async () => {
      const { data, error } = await supabase.auth.getUser();
      if (error) return;

      const u = data?.user || null;
      if (!u) return;

      setAuthUser(u);

      // 🚀 THE MARATHON RESUME: Check database for existing profile
      const { data: dbProfile } = await supabase
        .from("doctor_profiles")
        .select("*")
        .eq("user_id", u.id)
        .maybeSingle();

      if (!dbProfile) {
        const stored = loadProgress(LS_KEY) ?? {};
        const storedOwnerUserId = String(stored?.ownerUserId || "").trim();
        const storedHasName =
          String(stored?.firstName || "").trim() ||
          String(stored?.lastName || "").trim();

        if (storedOwnerUserId === u.id && storedHasName) {
          const seed = buildSeedState(u.id);
          saveProgress(LS_KEY, seed);
          setPhase(seed.phase);
          setStep(seed.step);
          setProfileStep(seed.profileStep);
          setFirstName(seed.firstName);
          setLastName(seed.lastName);
          setSalutation(seed.salutation);
          setEmail(seed.email);
          setPhone(seed.phone);
          setPhoneCc(seed.phoneCc);
          setE164(seed.e164);
          setAgreed(seed.agreed);
          setPasscodeDraft(seed.passcodeDraft);
          setPasscodeConfirm(seed.passcodeConfirm);
          setDateOfBirth(seed.dateOfBirth);
          setLanguages(seed.languages);
          setPreferredLanguage(seed.preferredLanguage);
          setSpecialties(seed.specialties);
          setSubspecialties(seed.subspecialties);
          setOcrSummary(seed.ocrSummary);
          setRegUpload(seed.regUpload);
          setFormation(seed.formation);
          setIdDoc(seed.idDoc);
          setIdVerification(seed.idVerification);
          setRegDetails(seed.regDetails);
          setAbout(seed.about);
        }
      }

      if (dbProfile) {
        // Sync local state from DB Truth
        if (dbProfile.first_name) setFirstName(dbProfile.first_name);
        if (dbProfile.last_name) setLastName(dbProfile.last_name);
        if (typeof dbProfile.salutation === "string") {
          setSalutation(dbProfile.salutation);
          persistDoctorOnboardingSnapshot({ salutation: dbProfile.salutation });
        }
        if (dbProfile.specialties) setSpecialties(dbProfile.specialties);
        if (dbProfile.languages_spoken) {
          const dbLanguages = normalizeStringList(dbProfile.languages_spoken);
          const dbPreferred = String(dbLanguages[0] || "").trim();
          setPreferredLanguage(dbPreferred);
          setLanguages(orderLanguagesWithPreferred(dbLanguages, dbPreferred));
          persistDoctorOnboardingSnapshot({
            languages: orderLanguagesWithPreferred(dbLanguages, dbPreferred),
            preferredLanguage: dbPreferred,
          });
        }
        if (dbProfile.about_you) setAbout(dbProfile.about_you);
        const dbRegDetails = normalizeDoctorRegDetailsFromProfile(dbProfile);
        if (
          dbRegDetails.number ||
          dbRegDetails.issuer ||
          dbRegDetails.issueDate ||
          dbRegDetails.expiryDate ||
          dbRegDetails.yearsExperience
        ) {
          const mergedRegDetails = {
            number: dbRegDetails.number || regDetails?.number || "",
            issuer: dbRegDetails.issuer || regDetails?.issuer || "",
            issueDate: dbRegDetails.issueDate || regDetails?.issueDate || "",
            expiryDate: dbRegDetails.expiryDate || regDetails?.expiryDate || "",
            yearsExperience:
              dbRegDetails.yearsExperience || regDetails?.yearsExperience || "",
          };
          setRegDetails(mergedRegDetails);
          persistDoctorOnboardingSnapshot({ regDetails: mergedRegDetails });
        }

        // JUMP LOGIC: Skip steps that are already in the DB
        if (phase === "intro") {
          if (dbProfile.first_name && dbProfile.last_name) {
            setStep((s) => Math.max(s, introStepIndex("phone")));
          }
          if (dbProfile.phone || u.phone) {
            setStep((s) => Math.max(s, introStepIndex(afterPhoneIntroStep)));
          }
          if (hasStoredPasscode(dbProfile)) {
            setStep((s) => Math.max(s, introStepIndex("dob")));
          }
          if (dbProfile.date_of_birth) {
            setDateOfBirth(String(dbProfile.date_of_birth || ""));
            setStep((s) => Math.max(s, introStepIndex("done")));
          }
        }
      }

    // ── Role + Trial meta glue (set once) ─────────────────────────────
      const isTrial14 = sp.get("trial") === "14";

      try {
        const meta = u.user_metadata || {};
        const needsRole = (meta.primary_role || "").toLowerCase() !== "doctor";
        const needsTrial = isTrial14 && String(meta.trial || "") !== "14";

        // Only write if missing/incorrect (prevents loops + overwrites)
        if (needsRole || needsTrial) {
          await supabase.auth.updateUser({
            data: {
              ...meta,
              primary_role: "doctor",
              ...(needsTrial ? { trial: "14" } : {}),
            },
          });
        }
      } catch {
        // Never block onboarding
      }

      // Seed contact values from Auth truth (preferred)
      if (u.email && !email) setEmail(u.email);
      if (u.phone) {
        const parsed = splitE164ToCcLocal(u.phone);
        if (parsed?.local && !phone) setPhone(parsed.local);
        if (parsed?.cc) setPhoneCc(parsed.cc);
        if (!e164) setE164(parsed?.e164 || u.phone);
      }

    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, phone, e164, introStepIndex, afterPhoneIntroStep]);

  useEffect(() => {
    setStep((current) => {
      const maxStep = Math.max(0, introSteps.length - 1);
      return Math.min(Math.max(current, 0), maxStep);
    });
  }, [introSteps.length]);

  // ---- Persist on any change ----
  useEffect(() => {
    saveProgress(LS_KEY, {
      phase,
      step,
      profileStep,
      ownerUserId: String(user?.id || authUser?.id || "").trim(),
      firstName,
      lastName,
      email,
      phone,
      phoneCc,
      e164,
      agreed,
      passcodeDraft,
      passcodeConfirm,
      dateOfBirth,
      languages,
      preferredLanguage,
      specialties,
      subspecialties,
      ocrSummary,
      regUpload,
      regDetails,
      idDoc,
      idVerification: normalizeIdVerificationState(idVerification),
      formation,
      about,      
    });
  }, [
    phase,
    step,
    profileStep,
    firstName,
    lastName,
    email,
    phone,
    phoneCc,
    e164,
    agreed,
    passcodeDraft,
    passcodeConfirm,
    dateOfBirth,
    languages,
    preferredLanguage,
    specialties,
    subspecialties,
    ocrSummary,
    regUpload,
    regDetails,
    idDoc,
    idVerification,
    formation,
    about,    
    user?.id,
    authUser?.id,
  ]);

  const persistDoctorOnboardingSnapshot = useCallback(
    (overrides = {}) => {
      const snapshot = {
        phase,
        step,
        profileStep,
        ownerUserId: String(user?.id || authUser?.id || "").trim(),
        firstName,
        lastName,
        salutation,
        email,
        phone,
        phoneCc,
        e164,
        agreed,
        passcodeDraft,
        passcodeConfirm,
        dateOfBirth,
        languages,
        preferredLanguage,
        specialties,
        subspecialties,
        ocrSummary,
        regUpload,
        regDetails,
        idDoc,
        idVerification: normalizeIdVerificationState(idVerification),
        formation,
        about,
        ...overrides,
      };
      snapshot.idVerification = normalizeIdVerificationState(snapshot.idVerification);
      snapshot.preferredLanguage = String(snapshot.preferredLanguage || "").trim();
      saveProgress(LS_KEY, snapshot);
      return snapshot;
    },
    [
      phase,
      step,
      profileStep,
      user?.id,
      authUser?.id,
      firstName,
      lastName,
      salutation,
      email,
      phone,
      phoneCc,
      e164,
      agreed,
      passcodeDraft,
      passcodeConfirm,
      dateOfBirth,
      languages,
      preferredLanguage,
      specialties,
      subspecialties,
      ocrSummary,
      regUpload,
      regDetails,
      idDoc,
      idVerification,
      formation,
      about,
    ]
  );

  const handleIdDocChange = useCallback(
    (nextValue) => {
      setIdDoc(nextValue);
      persistDoctorOnboardingSnapshot({ idDoc: nextValue });
    },
    [persistDoctorOnboardingSnapshot]
  );

  const handlePasscodeDraftChange = useCallback(
    (nextValue) => {
      setPasscodeDraft(nextValue);
      persistDoctorOnboardingSnapshot({ passcodeDraft: nextValue });
    },
    [persistDoctorOnboardingSnapshot]
  );

  const handlePasscodeConfirmChange = useCallback(
    (nextValue) => {
      setPasscodeConfirm(nextValue);
      persistDoctorOnboardingSnapshot({ passcodeConfirm: nextValue });
    },
    [persistDoctorOnboardingSnapshot]
  );

  const handleDobChange = useCallback(
    (nextValue) => {
      setDateOfBirth(nextValue);
      persistDoctorOnboardingSnapshot({ dateOfBirth: nextValue });
    },
    [persistDoctorOnboardingSnapshot]
  );

  const handleIdVerificationPersist = useCallback(
    (nextState) => {
      const normalized = normalizeIdVerificationState(nextState);
      setIdVerification(normalized);
      persistDoctorOnboardingSnapshot({ idVerification: normalized });
    },
    [persistDoctorOnboardingSnapshot]
  );

  const handleIdStepContinue = useCallback(
    (payload = {}) => {
      const nextIdDoc = String(payload?.idDoc ?? idDoc ?? "").trim().toUpperCase();
      const nextIdVerification = normalizeIdVerificationState(
        payload?.idVerification ?? idVerification
      );
      setIdDoc(nextIdDoc);
      setIdVerification(nextIdVerification);
      persistDoctorOnboardingSnapshot({
        idDoc: nextIdDoc,
        idVerification: nextIdVerification,
        profileStep: 1,
      });
      setProfileStep(1);
    },
    [idDoc, idVerification, persistDoctorOnboardingSnapshot]
  );

  useEffect(() => {
    const currentUserId = String(user?.id || "").trim();
    if (!currentUserId) return;

    const introStep = introSteps[step] || introSteps[0];
    const stepKey = mapDoctorOnboardingStepKey({
      phase,
      introStep,
      profileStep,
    });
    if (!stepKey || loggedStepKeysRef.current.has(stepKey)) return;
    loggedStepKeysRef.current.add(stepKey);

    void writeOnboardingEvent({
      userId: currentUserId,
      role: "doctor",
      step: stepKey,
      eventType: "step_entered",
      meta: {
        phase,
        intro_step: introStep,
        profile_step: profileStep,
      },
    }).catch((error) => {
      console.error("[doctor-onboarding] step tracking failed:", error?.message || error, {
        userId: currentUserId,
        step: stepKey,
      });
    });
  }, [user?.id, phase, step, profileStep, introSteps]);

  useEffect(() => {
    const currentUserId = String(user?.id || authUser?.id || "").trim();
    if (!currentUserId) return undefined;

    const currentEmail = String(user?.email || authUser?.email || "").trim().toLowerCase();
    const introStep = introSteps[step] || introSteps[0];
    const stepKey = mapDoctorOnboardingStepKey({
      phase,
      introStep,
      profileStep,
    });
    const currentPath =
      typeof window !== "undefined"
        ? getPrivacySafeTelemetryRoute(window.location)
        : "/onboarding/doctor";
    const targetRoute = currentPath || "/onboarding/doctor?role=doctor";

    const logPostAuthDiagnostic = (stage, details = {}, status = "success", errorMessage = null) => {
      if (!currentEmail) return;
      void logAuthAttempt({
        email: currentEmail,
        status,
        errorMessage,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        stage,
        currentPath,
        details: {
          userId: currentUserId,
          resolvedRole: "doctor",
          targetRoute,
          currentPath,
          onboardingStep: stepKey,
          onboardingPhase: phase,
          introStep,
          profileStep,
          timestamp: new Date().toISOString(),
          ...details,
        },
      }).catch((error) => {
        console.warn("[doctor-onboarding] post-auth diagnostic failed:", error?.message || error);
      });
    };

    const cookieSnapshotAtStart = readCookiePostAuthSnapshot();

    if (!postAuthDiagnosticsRef.current.started) {
      postAuthDiagnosticsRef.current.started = true;
      logPostAuthDiagnostic("post_auth_route_render_started", cookieSnapshotAtStart);
    }

    if (
      cookieSnapshotAtStart.cookieBannerVisible &&
      !postAuthDiagnosticsRef.current.cookieBannerVisible
    ) {
      postAuthDiagnosticsRef.current.cookieBannerVisible = true;
      logPostAuthDiagnostic("cookie_banner_visible_post_auth", cookieSnapshotAtStart);
    }

    if (!postAuthDiagnosticsRef.current.cookieChoice) {
      postAuthDiagnosticsRef.current.cookieChoice = true;
      logPostAuthDiagnostic("cookie_choice_post_auth", cookieSnapshotAtStart);
    }

    const settleTimer = window.setTimeout(() => {
      const cookieSnapshot = readCookiePostAuthSnapshot();
      const cta = findVisiblePostAuthCta();
      const firstCtaVisible = !!cta && !cookieSnapshot.cookieBannerVisible;
      const firstCtaEnabled = firstCtaVisible && isActionableElement(cta);
      postAuthDiagnosticsRef.current.ctaVisible = firstCtaVisible;

      if (!postAuthDiagnosticsRef.current.completed) {
        postAuthDiagnosticsRef.current.completed = true;
        logPostAuthDiagnostic("post_auth_route_render_completed", {
          ...cookieSnapshot,
          firstCtaVisible,
          firstCtaEnabled,
          firstCtaText: firstCtaVisible ? String(cta?.textContent || "").trim().slice(0, 80) : "",
        });
        logPostAuthDiagnostic("onboarding_first_screen_rendered", {
          ...cookieSnapshot,
          firstCtaVisible,
          firstCtaEnabled,
        });
      }

      if (firstCtaVisible && !postAuthDiagnosticsRef.current.ctaLogged) {
        postAuthDiagnosticsRef.current.ctaLogged = true;
        logPostAuthDiagnostic("onboarding_first_cta_visible", {
          ...cookieSnapshot,
          firstCtaVisible: true,
          firstCtaEnabled,
          firstCtaText: String(cta?.textContent || "").trim().slice(0, 80),
        });
      }

      if (
        cookieSnapshot.cookieBannerVisible &&
        !postAuthDiagnosticsRef.current.cookieBannerVisible
      ) {
        postAuthDiagnosticsRef.current.cookieBannerVisible = true;
        logPostAuthDiagnostic("cookie_banner_visible_post_auth", cookieSnapshot);
      }
    }, POST_AUTH_RENDER_SETTLE_MS);

    const blankTimer = window.setTimeout(() => {
      const cookieSnapshot = readCookiePostAuthSnapshot();
      const cta = findVisiblePostAuthCta();
      const firstCtaVisible = !!cta && !cookieSnapshot.cookieBannerVisible;
      if (firstCtaVisible || postAuthDiagnosticsRef.current.blankTimeout) return;

      postAuthDiagnosticsRef.current.blankTimeout = true;
      setPostAuthFallbackVisible(true);
      logPostAuthDiagnostic(
        "onboarding_blank_timeout",
        {
          ...cookieSnapshot,
          firstCtaVisible: false,
          firstCtaEnabled: false,
          timeoutMs: POST_AUTH_BLANK_TIMEOUT_MS,
        },
        "failed",
        "first_onboarding_cta_not_visible",
      );
    }, POST_AUTH_BLANK_TIMEOUT_MS);

    return () => {
      window.clearTimeout(settleTimer);
      window.clearTimeout(blankTimer);
    };
  }, [user?.id, user?.email, authUser?.id, authUser?.email, phase, step, profileStep, introSteps]);

  const handleRegUploadChange = useCallback(
    (nextValue) => {
      const merged = {
        ...(regUpload && typeof regUpload === "object" ? regUpload : {}),
        ...(nextValue && typeof nextValue === "object" ? nextValue : {}),
      };
      setRegUpload(merged);
      persistDoctorOnboardingSnapshot({ regUpload: merged });
    },
    [regUpload, persistDoctorOnboardingSnapshot]
  );

  const handleFormationChange = useCallback(
    (nextValue) => {
      setFormation(nextValue);
      persistDoctorOnboardingSnapshot({ formation: nextValue });
    },
    [persistDoctorOnboardingSnapshot]
  );

  const handleRegDetailsChange = useCallback(
    (nextValue) => {
      setRegDetails(nextValue);
      persistDoctorOnboardingSnapshot({ regDetails: nextValue });
    },
    [persistDoctorOnboardingSnapshot]
  );

// ---- Header counters ----
const INTRO_TOTAL = introSteps.length;
const PROFILE_TOTAL = 1;
const ALL_TOTAL = INTRO_TOTAL + PROFILE_TOTAL;

const headerStep =
  phase === "intro" ? step + 1 : INTRO_TOTAL + 1;
const headerTotal = phase === "intro" ? INTRO_TOTAL : ALL_TOTAL;
const H = { step: headerStep, total: headerTotal };
const currentIntroStep = introSteps[step] || introSteps[0];
const currentStepKey = mapDoctorOnboardingStepKey({
  phase,
  introStep: currentIntroStep,
  profileStep,
});
const contextNote = resumeContextVisible
  ? `${DOCTOR_SETUP_RESUME_LINE} ${DOCTOR_SETUP_REASSURANCE}`
  : DOCTOR_SETUP_REASSURANCE;

  const emitDoctorOnboardingActivity = useCallback(
    (eventName, metadata = {}) => {
      const currentUserId = String(user?.id || authUser?.id || "").trim();
      if (!currentUserId) return;
      const route =
        typeof window !== "undefined"
          ? getPrivacySafeTelemetryRoute(window.location)
          : "/onboarding/doctor";
      void writeUserActivityEvent({
        userId: currentUserId,
        role: "doctor",
        route,
        eventType: `onboarding_${eventName}`,
        metadata: {
          step: currentStepKey,
          phase,
          intro_step: currentIntroStep,
          profile_step: profileStep,
          value_recorded: false,
          ...getDocumentNavigationTelemetry(),
          ...metadata,
        },
      }).catch((error) => {
        console.warn("[doctor-onboarding] activity tracking failed:", error?.message || error);
      });
    },
    [user?.id, authUser?.id, currentStepKey, phase, currentIntroStep, profileStep]
  );

  const telemetryForCurrentStep = useCallback(
    (eventName, metadata = {}) => {
      emitDoctorOnboardingActivity(eventName, metadata);
    },
    [emitDoctorOnboardingActivity]
  );

  useEffect(() => {
    emitDoctorOnboardingActivity("step_rendered");
  }, [emitDoctorOnboardingActivity]);

  useEffect(() => {
    if (shellMountRecordedRef.current) return;
    shellMountRecordedRef.current = true;
    doctorOnboardingShellMountsForDocument += 1;
    emitDoctorOnboardingActivity("shell_mounted", {
      ...getDocumentNavigationTelemetry(),
      shell_mount_sequence: doctorOnboardingShellMountsForDocument,
    });
  }, [emitDoctorOnboardingActivity]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onBeforeUnload = () => {
      emitDoctorOnboardingActivity("user_left_or_reloaded", {
        detectable_reason: "beforeunload",
      });
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [emitDoctorOnboardingActivity]);

  // ---- Back helpers ----
  const backFirst = useCallback(() => {
    if (typeof backToCategory === "function") {
      backToCategory();
    } else {
      navigate("/onboarding", { replace: true });
    }
  }, [backToCategory, navigate]);

  const backIntro = () => setStep((s) => Math.max(0, s - 1));
  const backProfile = () => setProfileStep((s) => Math.max(0, s - 1));

  if (postAuthFallbackVisible) {
    return (
      <PostAuthSetupFallback
        onRetry={() => window.location.reload()}
        onSignIn={() => navigate("/login?reauth=1", { replace: true })}
      />
    );
  }

  /* -------------------- RENDER -------------------- */
if (phase === "intro") {
  const currentIntroStep = introSteps[step] || introSteps[0];
  const introBack = step === 0 ? backFirst : backIntro;
  const nextIntro = () =>
    setStep((s) => Math.min(s + 1, Math.max(0, introSteps.length - 1)));

  switch (currentIntroStep) {
    case "name":
      return (
        <StepName
          {...H}
          profileTable="doctor_profiles"
          contextNote={contextNote}
          onTelemetry={telemetryForCurrentStep}
          first={firstName}
          last={lastName}
          salutation={salutation}
          onChangeFirst={(next) => {
            setFirstName(next);
            persistDoctorOnboardingSnapshot({ firstName: next });
          }}
          onChangeLast={(next) => {
            setLastName(next);
            persistDoctorOnboardingSnapshot({ lastName: next });
          }}
          onChangeSalutation={(next) => {
            setSalutation(next);
            persistDoctorOnboardingSnapshot({ salutation: next });
          }}
          onBack={introBack}
          onContinue={nextIntro}
        />
      );

    case "phone":
      return (
        <StepPhone
          {...H}
          value={phone}
          initialCc={phoneCc}
          agreed={agreed}
          contextNote={contextNote}
          onTelemetry={telemetryForCurrentStep}
          onChangePhone={setPhone}
          onChangeCc={setPhoneCc}
          onChangeAgreed={setAgreed}
          onBack={introBack}
          onContinue={async (valE164) => {
            const normalizedPhone = String(valE164 || "").trim();
            let canonicalDoctorUserId = String(authUser?.id || "").trim();
            if (!canonicalDoctorUserId) {
              try {
                const { data } = await supabase.auth.getUser();
                canonicalDoctorUserId = String(data?.user?.id || "").trim();
              } catch {
                canonicalDoctorUserId = "";
              }
            }
            if (!canonicalDoctorUserId || !normalizedPhone) {
              throw new Error("doctor_phone_persistence_unavailable");
            }
            await saveVerificationPhone({
              phone: normalizedPhone,
              role: "doctor",
              userId: canonicalDoctorUserId,
            });
            setE164(normalizedPhone);
            nextIntro();
          }}
        />
      );

      case "otp":
        if (!ENABLE_ONBOARDING_PHONE_OTP) {
          return null;
        }
        return (
          <StepOtp
          {...H}
          phone={String(e164 || authUser?.phone || "").trim()}
          profileTable="doctor_profiles"
          contextNote={contextNote}
          onTelemetry={telemetryForCurrentStep}
          onBack={introBack}
          onContinue={nextIntro}
        />
      );

    case "passcodeCreate":
      return (
        <StepPasscodeCreate
          {...H}
          profileTable="doctor_profiles"
          contextNote={contextNote}
          onTelemetry={telemetryForCurrentStep}
          value={passcodeDraft}
          onChange={handlePasscodeDraftChange}
          onBack={introBack}
          onContinue={nextIntro}
        />
      );

    case "passcodeConfirm":
      return (
        <StepPasscodeConfirm
          {...H}
          profileTable="doctor_profiles"
          contextNote={contextNote}
          onTelemetry={telemetryForCurrentStep}
          original={passcodeDraft}
          value={passcodeConfirm}
          onChange={handlePasscodeConfirmChange}
          onBack={introBack}
          onContinue={nextIntro}
        />
      );

    case "dob":
      return (
        <StepDob
          {...H}
          profileTable="doctor_profiles"
          contextNote={contextNote}
          onTelemetry={telemetryForCurrentStep}
          value={dateOfBirth}
          onChange={handleDobChange}
          onBack={introBack}
          onContinue={nextIntro}
        />
      );

    case "about":
      return (
        <StepAbout
          {...H}
          value={about}
          folaHint={{
            degree: "",
            specialty: "",
            subspecialty: "",
            subSpecialties: [],
            institution: "",
            fieldOfStudy: "",
            qualifications: "",
            languages: "",
            yearsExperience: "",
            verificationStatus: "needs_verification",
            registrationVerified: false,
            trainingSummary: "",
            roleTitle: "Clinician",
            salutation,
            firstName,
            lastName,
            fullName: [firstName, lastName].map((item) => String(item || "").trim()).filter(Boolean).join(" "),
            doctorDisplayName: [salutation, String(lastName || "").trim()].filter(Boolean).join(" "),
          }}
          contextNote={contextNote}
          onTelemetry={telemetryForCurrentStep}
          onChange={setAbout}
          onBack={introBack}
          onContinue={nextIntro}
        />
      );

    case "profileComplete":
      return (
        <StepProfileComplete
          {...H}
          onBack={introBack}
        />
      );

    case "done":
      return (
        <StepDoneDoctor
          {...H}
          fallbackPhone={String(e164 || "").trim()}
          contextNote={contextNote}
          onTelemetry={telemetryForCurrentStep}
          onBack={introBack}
          onSkip={() => {
            // First-use completion enters Settings once so the welcome dialog
            // and Fola guidance can take over before any later Home landing.
            void markSettingsWelcomePending(user?.id, "doctor");
            navigate("/settings/doctor", {
              replace: true,
              state: { showSettingsWelcome: true },
            });
          }}
        />
      );

    default:
  return null;
  }
}

  if (phase === "profile") {
    if (!shouldUseDeferredOnboardingProfileSteps()) {
      return (
        <StepProfileComplete
          {...H}
          onBack={() => {
            setPhase("intro");
            setStep(Math.max(0, introSteps.length - 1));
          }}
        />
      );
    }

    switch (profileStep) {
      case 0:
        return (
          <StepIDPassport
            {...H}
            profileTable="doctor_profiles" // ✅ Correct: Tells the AI where to save
            value={idDoc}
            onChange={handleIdDocChange}
            persistedState={idVerification}
            onPersistState={handleIdVerificationPersist}
            onBack={() => {
              setPhase("intro");
              setStep(Math.max(0, introSteps.length - 1));
            }}
            onContinue={handleIdStepContinue} 
          />
        );

      case 1: // In Phase: Profile
        return (
          <StepLanguages
            {...H}
            profileTable="doctor_profiles"
            selected={languages}
            preferred={preferredLanguage || (Array.isArray(languages) ? languages[0] : "") || ""}
            onPreferredChange={(nextPreferred) => {
              const preferred = String(nextPreferred || "").trim();
              setPreferredLanguage(preferred);
              const ordered = [
                preferred,
                ...(
                  Array.isArray(languages) ? languages : []
                )
                  .map((item) => String(item || "").trim())
                  .filter(Boolean)
                  .filter((item) => item.toLowerCase() !== preferred.toLowerCase()),
              ].filter(Boolean);
              setLanguages(ordered);
              persistDoctorOnboardingSnapshot({
                preferredLanguage: preferred,
                languages: ordered,
              });
            }}
            onChange={(next) => {
              setLanguages(next);
              persistDoctorOnboardingSnapshot({ languages: next, preferredLanguage });
            }}
            onBack={() => setProfileStep(0)}
            onContinue={() => {
              persistDoctorOnboardingSnapshot({
                languages,
                preferredLanguage: preferredLanguage || (Array.isArray(languages) ? languages[0] : "") || "",
                profileStep: 2,
              });
              setProfileStep(2);
            }}
          />
        );

      case 2:
        return (
          <StepSpecialty
            {...H}
            selected={specialties}
            onTelemetry={telemetryForCurrentStep}
            onChange={(next) => {
              telemetryForCurrentStep("field_changed", { field: "specialty", value_recorded: false });
              setSpecialties(next);
            }}
            onBack={backProfile}
            onContinue={() => {
              telemetryForCurrentStep("continue_clicked", { valid: true });
              setProfileStep(3);
            }}
          />
        );

      case 3:
        return (
          <StepSubSpecialty
            {...H}
            selected={subspecialties}
            onChange={setSubspecialties}
            onBack={backProfile}
            onContinue={() => setProfileStep(4)}
          />
        );

      case 4: // StepRegUpload
        return (
          <StepRegUpload
            {...H}
            value={regUpload}
            onChange={handleRegUploadChange}
            onBack={backProfile} // ✅ Correct onBack preserved
            onContinue={(payload) => {
              const registrationDeferred = payload?.stagingDeferred === true;
              const hasRealRegistrationOcr =
                !registrationDeferred &&
                !!payload?.uploadMeta &&
                !!payload?.uploadMeta?.ocrSummary;
              const nextRegUpload = {
                files: [],
                consent: payload?.consent === true,
                uploadMeta: payload?.uploadMeta || null,
                ocrSummary: hasRealRegistrationOcr ? payload.uploadMeta.ocrSummary : null,
                ocrStatus: hasRealRegistrationOcr ? "done" : registrationDeferred ? "deferred" : "idle",
                fileName:
                  payload?.files?.[0]?.name ||
                  String(payload?.uploadMeta?.path || "").split("/").pop() ||
                  "",
                stagingDeferred: registrationDeferred,
                stagingTesterEmail: payload?.stagingTesterEmail || "",
              };
              // 1. Save the full summary for the "Beat" results and Fola to see
              const summary = hasRealRegistrationOcr ? payload.uploadMeta.ocrSummary : null;
              const ocrRegDetails = summary?.fields
                ? {
                    number: summary.fields.document_number || "",
                    issuer: summary.fields.issuer || summary.fields.nationality || "",
                    issueDate: formatToIso(summary.fields.issue_date || ""),
                    expiryDate: summary.fields.expiry_date
                      ? formatToIso(summary.fields.expiry_date)
                      : "",
                    yearsExperience: "",
                  }
                : null;
              const nextRegDetails = {
                number:
                  registrationDeferred ? "" : regDetails?.number ||
                  persistedProfileRegDetails.number ||
                  ocrRegDetails?.number ||
                  "",
                issuer:
                  registrationDeferred ? "" : regDetails?.issuer ||
                  persistedProfileRegDetails.issuer ||
                  ocrRegDetails?.issuer ||
                  "",
                issueDate:
                  registrationDeferred ? "" : regDetails?.issueDate ||
                  persistedProfileRegDetails.issueDate ||
                  ocrRegDetails?.issueDate ||
                  "",
                expiryDate:
                  registrationDeferred ? "" : regDetails?.expiryDate ||
                  persistedProfileRegDetails.expiryDate ||
                  ocrRegDetails?.expiryDate ||
                  "",
                yearsExperience:
                  registrationDeferred ? "" : regDetails?.yearsExperience ||
                  persistedProfileRegDetails.yearsExperience ||
                  "",
                ...(registrationDeferred
                  ? {
                      stagingDeferred: true,
                      verificationStatus: "needs_verification",
                    }
                  : {}),
              };

              setRegUpload(nextRegUpload);
              setOcrSummary(summary);

              // Preserve reviewed registration truth; OCR should only seed missing values.
              setRegDetails(nextRegDetails);
              persistDoctorOnboardingSnapshot({
                regUpload: nextRegUpload,
                ocrSummary: summary,
                regDetails: nextRegDetails,
                profileStep: 5,
              });
              // ✅ Step 5 is StepFormation
              setProfileStep(5);
            }}
          />
        );

      case 5:
        return (
          <StepFormation
            {...H}
            // ✅ THE SAFE HANDSHAKE: Added optional chaining (?.)
            value={{
              ...formation,
              institution:
                regUpload?.stagingDeferred === true
                  ? formation?.institution || ""
                  : formation?.institution || ocrSummary?.fields?.institution || "",
              degree:
                regUpload?.stagingDeferred === true
                  ? formation?.degree || ""
                  : formation?.degree || ocrSummary?.fields?.degree || "",
            }}
            onChange={handleFormationChange}
            contextNote={contextNote}
            onTelemetry={telemetryForCurrentStep}
            onBack={backProfile}
            onContinue={(val) => {
              const nextFormation = {
                institution: val?.institution || "",
                degree: val?.degree || "",
                fieldOfStudy: val?.fieldOfStudy || val?.field_of_study || "",
                startDate: val?.startDate || val?.start_date || "",
                endDate: val?.endDate || val?.end_date || "",
                achievement: val?.achievement || "",
              };
              setFormation(nextFormation);
              persistDoctorOnboardingSnapshot({
                formation: nextFormation,
                profileStep: 6,
              });
              setProfileStep(6);
            }}
          />
        );

      case 6:
        return (
          <StepRegDetails
            {...H}
            value={{
              ...(regUpload?.stagingDeferred === true ? {} : persistedProfileRegDetails),
              ...regDetails,
              number:
                regUpload?.stagingDeferred === true ? regDetails.number || "" :
                regDetails.number ||
                persistedProfileRegDetails.number ||
                ocrSummary?.fields?.document_number ||
                "",
              issuer:
                regUpload?.stagingDeferred === true ? regDetails.issuer || "" :
                regDetails.issuer ||
                persistedProfileRegDetails.issuer ||
                ocrSummary?.fields?.issuer ||
                "",
              issueDate:
                regUpload?.stagingDeferred === true ? regDetails.issueDate || "" :
                regDetails.issueDate ||
                persistedProfileRegDetails.issueDate ||
                formatToIso(ocrSummary?.fields?.issue_date || ""),
              expiryDate:
                regUpload?.stagingDeferred === true ? regDetails.expiryDate || "" :
                regDetails.expiryDate ||
                persistedProfileRegDetails.expiryDate ||
                formatToIso(ocrSummary?.fields?.expiry_date || ""),
              yearsExperience:
                regUpload?.stagingDeferred === true ? regDetails.yearsExperience || "" :
                regDetails.yearsExperience ||
                persistedProfileRegDetails.yearsExperience ||
                "",
            }}
            onChange={handleRegDetailsChange}
            onBack={backProfile}
            onContinue={(finalData) => {
              setRegDetails(finalData);
              persistDoctorOnboardingSnapshot({
                regDetails: finalData,
                profileStep: 7,
              });
              setProfileStep(7); // Move to StepAbout
            }}
          />
        );

      case 7:
        return (
          <StepAbout
            {...H}
            value={about}
            folaHint={{
              degree:
                formation?.degree ||
                formation?.qualification ||
                formation?.fieldOfStudy ||
                formation?.field_of_study ||
                ocrSummary?.fields?.degree ||
                "",
              specialty:
                (Array.isArray(specialties) ? specialties.find((item) => String(item || "").trim()) : "") ||
                ocrSummary?.fields?.specialty ||
                "",
              subspecialty:
                (Array.isArray(subspecialties) ? subspecialties.map((item) => String(item || "").trim()).filter(Boolean).join(", ") : "") ||
                "",
              subSpecialties: Array.isArray(subspecialties)
                ? subspecialties.map((item) => String(item || "").trim()).filter(Boolean)
                : [],
              institution:
                formation?.institution ||
                ocrSummary?.fields?.institution ||
                "",
              fieldOfStudy:
                formation?.fieldOfStudy ||
                formation?.field_of_study ||
                "",
              qualifications:
                [
                  formation?.degree,
                  formation?.fieldOfStudy,
                  formation?.field_of_study,
                  ocrSummary?.fields?.degree,
                ]
                  .map((item) => String(item || "").trim())
                  .filter(Boolean)
                  .join(", "),
              languages:
                Array.isArray(languages)
                  ? languages.map((item) => String(item || "").trim()).filter(Boolean).join(", ")
                  : "",
              yearsExperience: regUpload?.stagingDeferred === true ? "" : regDetails?.yearsExperience || "",
              verificationStatus: regUpload?.stagingDeferred === true ? "needs_verification" : "",
              registrationVerified: false,
              trainingSummary:
                [
                  formation?.institution,
                  formation?.degree,
                  formation?.fieldOfStudy || formation?.field_of_study,
                  formation?.startDate,
                  formation?.endDate,
                  formation?.achievement,
                ]
                  .map((item) => String(item || "").trim())
                  .filter(Boolean)
                  .join(" | "),
              roleTitle: "Clinician",
              salutation,
              firstName,
              lastName,
              fullName: [firstName, lastName].map((item) => String(item || "").trim()).filter(Boolean).join(" "),
              doctorDisplayName: [salutation, String(lastName || "").trim()].filter(Boolean).join(" "),
            }}
            onChange={setAbout}
            onBack={backProfile}
            onContinue={() => setProfileStep(8)}
          />
        );

      case 8:
        return (
          <StepProfileComplete
            {...H}
            onBack={backProfile}
          />
        );

      default:
    return null;
    }
  }

  return null;
}
